import { useAdminStats } from '../../hooks/useAdminStats';
import { Spinner } from '../../components/ui/Spinner';
import { Link } from 'react-router-dom';

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: '#10b981',
  PENDING: '#f59e0b',
  EXPIRED: '#6b7280',
  CANCELLED: '#ef4444',
  REJECTED: '#ef4444',
};

function timeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'hoy';
  if (days === 1) return 'hace 1 día';
  return `hace ${days} días`;
}

export default function AdminResumenPage() {
  const { data, isLoading, error } = useAdminStats();

  if (isLoading) return <div className="flex justify-center pt-20"><Spinner size={40} /></div>;
  if (error || !data) return <p className="text-red-400">Error al cargar estadísticas.</p>;

  const { summary, users } = data;

  const kpis = [
    { label: 'Usuarios totales', value: summary.totalUsers, color: '#f59e0b' },
    { label: 'Activos 7 días', value: summary.activeUsers7d, color: '#10b981' },
    { label: 'Trades totales', value: summary.totalTrades, color: '#60a5fa' },
    { label: 'Países', value: summary.countries.length, color: '#a78bfa' },
  ];

  const recentUsers = [...users]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  return (
    <div>
      <h1 className="text-lg font-semibold text-white mb-4">Resumen</h1>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {kpis.map(({ label, value, color }) => (
          <div key={label} className="bg-[#1a1a2e] rounded-lg p-4 text-center">
            <div className="text-3xl font-bold mb-1" style={{ color }}>{value}</div>
            <div className="text-xs text-white/50">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Trades por estado */}
        <div className="bg-[#1a1a2e] rounded-lg p-4">
          <h2 className="text-sm font-semibold text-white/70 mb-3">Trades por estado</h2>
          <div className="flex flex-col gap-2">
            {Object.entries(summary.tradesByStatus).map(([status, count]) => (
              <div key={status} className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: STATUS_COLORS[status] ?? '#888' }}
                  />
                  <span className="text-sm text-white/70">{status}</span>
                </div>
                <span className="text-sm font-bold" style={{ color: STATUS_COLORS[status] ?? '#888' }}>
                  {count}
                </span>
              </div>
            ))}
            {Object.keys(summary.tradesByStatus).length === 0 && (
              <p className="text-sm text-white/40">Sin trades aún.</p>
            )}
          </div>
        </div>

        {/* Últimos registros */}
        <div className="bg-[#1a1a2e] rounded-lg p-4">
          <h2 className="text-sm font-semibold text-white/70 mb-3">Últimos registros</h2>
          <div className="flex flex-col gap-2">
            {recentUsers.map((u) => (
              <div key={u.userId} className="flex justify-between items-center">
                <span className="text-sm text-gold-400">{u.username}</span>
                <span className="text-xs text-white/40">{timeAgo(u.createdAt)}</span>
              </div>
            ))}
          </div>
          <Link to="/admin/usuarios" className="block mt-3 text-xs text-gold-400 hover:underline">
            ver todos →
          </Link>
        </div>
      </div>
    </div>
  );
}
